import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, Shield, Award, Home as HomeIcon, Heart, Instagram } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { api } from "../api";
import { TypeOnView } from "../components/TypeOnView";
import { Typewriter } from "../components/Typewriter";
import { ReviewsMarquee } from "../components/ReviewsMarquee";
import { LeadForm } from "../components/LeadForm";
import { Map } from "../components/Map";
import { SEO } from "../components/SEO";
import { SITE, IMG, VIDEO_URL, CITIES } from "../data/siteData";

const programs = [
  {
    title: "Board & Train",
    desc: "Our exclusive board and train program is the most effective way to get the highest quality of training for you and your dog — covering the widest range of education and behaviour modification for any breed and any issue.",
    img: IMG.boardTrain,
    to: "/services/board-and-train",
    span: "md:col-span-7",
  },
  {
    title: "Private Training",
    desc: "A training plan customized to you and your dog's specific needs. One-on-one coaching with unlimited phone and email support during and after your program.",
    img: IMG.privateTraining,
    to: "/services/private-training",
    span: "md:col-span-5",
  },
  {
    title: "Jumpstart Program",
    desc: "A 2-week board and train giving your dog or puppy a solid foundation of on-leash communication, manners and socialization.",
    img: IMG.jumpstart,
    to: "/services/jumpstart",
    span: "md:col-span-12",
  },
];

const values = [
  { icon: Shield, title: "Behaviour Modification Experts", desc: "From leash pulling to serious aggression and separation anxiety — we rehabilitate the cases other trainers turn away." },
  { icon: Award, title: "Certified Professional Trainers", desc: "Round-the-clock professional training and care at our large indoor facility and outdoor acreage just outside Calgary." },
  { icon: HomeIcon, title: "Owner Education Included", desc: "Mid-way, go-home and follow-up sessions ensure you can confidently continue your dog's training at home." },
  { icon: Heart, title: "Lifetime Support", desc: "Free consultation via phone, text or email after your program, plus discounts on future training and boarding." },
];


const InstagramSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/instagram")
      .then((r) => {
        if (r.data.configured && r.data.posts.length) setPosts(r.data.posts);
      })
      .catch(() => {});
  }, []);

  const tiles = posts.length
    ? posts.map((p) => ({ img: p.url, href: p.permalink, alt: p.caption || "Chinook K9 Instagram post" }))
    : [...IMG.gallery, IMG.boardTrain, IMG.jumpstart].map((src, i) => ({
        img: src, href: SITE.instagram, alt: `Chinook K9 Instagram training post ${i + 1}`,
      }));

  return (
    <section className="bg-white py-24 sm:py-32" data-testid="instagram-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Follow Along</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
              <TypeOnView text="@chinookk9 on Instagram" />
            </h2>
          </div>
          <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="instagram-follow-btn"
            className="inline-flex items-center gap-2 bg-obsidian text-white font-heading font-bold text-sm px-6 py-3 rounded-full hover:bg-brand transition-colors shrink-0">
            <Instagram size={16} /> Follow Us
          </a>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {tiles.map((t, i) => (
            <Reveal key={t.img || i} delay={i * 0.06}>
              <a href={t.href} target="_blank" rel="noreferrer" data-testid={`instagram-tile-${i}`}
                className="group relative block aspect-square rounded-xl overflow-hidden">
                <img src={t.img} alt={t.alt} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-brand/0 group-hover:bg-brand/70 transition-colors flex items-center justify-center">
                  <Instagram size={26} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    ["touchstart", "click", "scroll"].forEach((evt) =>
      window.addEventListener(evt, tryPlay, { once: true, passive: true })
    );
    return () => {
      ["touchstart", "click", "scroll"].forEach((evt) => window.removeEventListener(evt, tryPlay));
    };
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Chinook K9 | Dog Training Calgary & Surrounding Areas"
        description="Certified professional dog training serving Calgary, Airdrie, Cochrane, Okotoks & Chestermere. Board & Train, private lessons and puppy programs. Call 403-715-0499."
        path="/"
      />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <img
          src={IMG.heroPoster}
          alt="Dog training in action at Chinook K9"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
        />
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover object-center bg-black transition-opacity duration-700 ${videoPlaying ? "opacity-100" : "opacity-0"}`}
          src={VIDEO_URL}
          autoPlay loop muted playsInline preload="auto" disablePictureInPicture controls={false}
          onPlaying={() => setVideoPlaying(true)}
          data-testid="hero-video"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
            className="text-brand font-heading font-bold tracking-[0.3em] text-xs sm:text-sm uppercase mb-6 hero-text-shadow"
          >
            Serving Calgary & Surrounding Areas
          </motion.p>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tighter text-white leading-[1.05] max-w-4xl hero-text-shadow">
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.35, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                Certified Professional
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                Dog Training For A
              </motion.span>
            </span>
            <span className="block overflow-hidden text-brand">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.65, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                <Typewriter words={["Well-Behaved Companion.", "Confident Off-Leash Dog.", "New Puppy for your Family!", "Dog You Can Take Anywhere."]} />
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="mt-7 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed hero-text-shadow"
          >
            We are dedicated to providing dogs and their owners with the tools, knowledge and skills needed to reach
            their goals — whether you're working through behavioural issues or building a stronger relationship with your dog.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center justify-center md:justify-start gap-4"
          >
            <Link to="/services/board-and-train" data-testid="hero-start-today-btn"
              className="bg-brand text-white font-heading font-bold px-8 py-4 rounded-full hover:bg-red-700 transition-colors shadow-[0_0_25px_rgba(220,38,38,0.5)] flex items-center gap-2">
              Board & Train! <ArrowRight size={18} />
            </Link>
            <a href={SITE.phoneHref} data-testid="hero-phone-btn"
              className="border border-white/30 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-colors flex items-center gap-2 backdrop-blur-sm">
              <Phone size={17} /> {SITE.phone}
            </a>
          </motion.div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsMarquee />

      {/* QUOTE / ABOUT */}
      <section className="bg-white text-slate-900 py-24 sm:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-5">Our Philosophy</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              "Most dog training is about teaching your dog to <span className="text-brand">DO</span> something, when it
              should really be about teaching your dog to <span className="text-brand">BE</span> something."
            </h2>
            <p className="mt-6 font-heading font-semibold text-slate-500">~ Dylan Rowse, Owner/Trainer</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 border-2 border-brand/30 rounded-2xl translate-x-4 translate-y-4" />
              <img src={IMG.philosophy} alt="Dylan Rowse training a dog at the Chinook K9 facility" className="relative rounded-2xl w-full h-auto" data-testid="about-image" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROGRAMS BENTO */}
      <section className="bg-obsidian py-24 sm:py-32" id="programs" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Training Programs</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
              <TypeOnView text="A program for every dog, every goal" />
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {programs.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12} className={p.span}>
                <Link to={p.to} data-testid={`program-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block relative rounded-2xl overflow-hidden h-[340px] border border-white/10">
                  <img src={p.img} alt={`${p.title} at Chinook K9`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 p-8">
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">{p.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed max-w-xl line-clamp-2">{p.desc}</p>
                    <span className="inline-flex items-center gap-2 mt-5 bg-brand text-white font-heading font-bold text-sm px-6 py-2.5 rounded-full group-hover:bg-red-700 group-hover:gap-3 transition-all shadow-[0_0_15px_rgba(220,38,38,0.35)]">
                      Learn More <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white text-slate-900 py-24 sm:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Why Chinook K9</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              <TypeOnView text="More than obedience — a better life with your dog" />
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border border-black/10 rounded-2xl p-7 h-full hover:border-brand/50 hover:shadow-xl transition-[border-color,box-shadow]" data-testid={`value-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-5">
                    <v.icon size={22} />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2.5">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM PREVIEW */}
      <InstagramSection />

      {/* SERVICE AREAS */}
      <section className="bg-obsidian py-24 sm:py-32 border-y border-white/10" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Service Areas</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
              <TypeOnView text="Dog training near you" />
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CITIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link to={`/dog-training/${c.slug}`} data-testid={`area-card-${c.slug}`}
                  className="group flex items-center justify-between bg-[#121212] border border-white/10 rounded-xl px-6 py-5 hover:border-brand/60 transition-colors">
                  <div>
                    <h3 className="font-heading font-bold text-white text-lg">Dog Training {c.name}</h3>
                    <p className="text-white/50 text-sm mt-0.5">Alberta</p>
                  </div>
                  <ArrowRight size={18} className="text-brand group-hover:translate-x-1 transition-transform" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="bg-obsidian py-24 sm:py-32" id="lead-form" data-testid="home-lead-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal className="mb-10 text-center">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Get Started</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
              <TypeOnView text="Tell us about your dog" />
            </h2>
            <p className="mt-4 text-white/60">The first step to a well-trained dog is just a click away.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <LeadForm />
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-white py-24 sm:py-32" data-testid="home-map-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <Reveal className="lg:col-span-2">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Visit Us</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              <TypeOnView text="Located minutes from Calgary" />
            </h2>
            <ul className="space-y-4 text-slate-700">
              <li className="font-semibold">{SITE.address}</li>
              <li><a href={SITE.phoneHref} className="text-brand font-semibold hover:underline" data-testid="map-phone">{SITE.phone}</a></li>
              <li><a href={`mailto:${SITE.email}`} className="text-brand font-semibold hover:underline" data-testid="map-email">{SITE.email}</a></li>
              <li className="text-slate-600">{SITE.hours}</li>
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <Map height={440} />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
